import React, { useEffect } from "react"

import {
  Flex,
  Text,
  Icon,
  List,
  Badge,
  VStack,
  Button,
  Heading,
  ListIcon,
  ListItem,
  Skeleton,
  Box,
  SkeletonText,
  SkeletonCircle,
  Fade,
} from "@chakra-ui/react"
import { NumberOfTriesAction, RiskMeasure } from "../types/types"
import {
  bookDemoMessage,
  demoResultText1,
  demoResultText2,
  demoResultText3,
  freeTrialLink,
} from "../constants/texts"
import { useEventSource } from "../hooks/useEventSource"
const CircleIcon: React.FC<any> = (props) => (
  <Icon viewBox="0 0 200 200" {...props}>
    <path
      fill="currentColor"
      d="M 100, 100 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0"
    />
  </Icon>
)
export const AIResults = ({
  handleTryAgain,
  dispatchNumberOfTries,
  projectDescription,
  companyDescription,
}: {
  handleTryAgain: () => void
  dispatchNumberOfTries: (action: NumberOfTriesAction) => void
  projectDescription: string
  companyDescription: string
}) => {
  const [risksMeasures, setRisksMeasures] = React.useState<RiskMeasure[]>([])
  const { risks, measures, errors } = useEventSource({
    projectDescription,
    companyDescription,
    dispatchNumberOfTries,
  })

  useEffect(() => {
    const newRisksMeasures = measures.reduce(
      (riskMeasures: RiskMeasure[], measure) => {
        const riskIndex = riskMeasures.findIndex(
          (riskMeasure) => riskMeasure.id === measure.parentid
        )
        if (riskIndex !== -1) {
          const newRiskMeasures = [...riskMeasures]
          newRiskMeasures[riskIndex].measures.push(measure)
          return newRiskMeasures
        } else {
          const risk = risks.find((r) => r.id === measure.parentid)
          if (risk) {
            return [...riskMeasures, { ...risk, measures: [measure] }]
          }
        }
        return riskMeasures
      },
      []
    )
    setRisksMeasures(newRisksMeasures)
  }, [risks, measures])

  return (
    <>
      {errors.length > 0 && (
        <Flex direction="column" align="center">
          <Heading color="dmazespecial.400" as="h2">
            Something went wrong
          </Heading>
          <Button
            marginTop="24px"
            variant="link"
            onClick={() => handleTryAgain()}
          >
            Try again
          </Button>
        </Flex>
      )}
      {errors.length === 0 && (
        <Flex direction="column">
          <Heading color="dmazespecial.400" as="h2">
            Results
          </Heading>
          <VStack spacing="24px" marginBottom="48px">
            <Text>{demoResultText1}</Text>
            <Text>{demoResultText2}</Text>
            <Text>{demoResultText3}</Text>
            <Button onClick={() => (window.location.href = freeTrialLink)}>
              {bookDemoMessage}
            </Button>
            <Button
              _hover={{ textDecoration: "none" }}
              variant="link"
              onClick={() => handleTryAgain()}
            >
              <Text
                _hover={{ textDecoration: "none" }}
                as="u"
                variant="underline"
              >
                Try again
              </Text>
            </Button>
          </VStack>
          <Flex direction="column" align="center" width="100%">
            {(risksMeasures.length > 0 &&
              risksMeasures.map((risk, index) => (
                <Flex key={`risk-${index}`} direction="column" width="100%">
                  <Flex
                    paddingTop="24px"
                    paddingBottom="24px"
                    style={{
                      borderBottom: "1px solid",
                      borderTop: "1px solid",
                      borderColor: "#E0E4E8",
                    }}
                  >
                    <Fade in={true}>
                      <Heading
                        display="flex"
                        alignItems="center"
                        fontWeight="normal"
                        color="dmazegray.900"
                        size="sm"
                      >
                        <Badge
                          borderRadius="14px"
                          color="white"
                          bg="dmazespecial.600"
                          fontSize="md"
                          width="28px"
                          height="28px"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          {index + 1}
                        </Badge>
                        &nbsp;&nbsp;
                        {risk.title}
                      </Heading>
                    </Fade>
                  </Flex>
                  <Box marginLeft="38px" paddingBottom="24px" paddingTop="24px">
                    <List>
                      {risk.measures.map((measure) => (
                        <Fade
                          key={`measure-${measure.id}-${index}`}
                          in={true}
                          style={{ display: "flex" }}
                        >
                          <ListIcon
                            verticalAlign="unset"
                            boxSize="9px"
                            as={CircleIcon}
                            color="dmazespecial.400"
                            marginTop="8px"
                          />
                          <ListItem key={`measure-${index}`}>
                            {measure.title}
                          </ListItem>
                        </Fade>
                      ))}
                    </List>
                  </Box>
                </Flex>
              ))) || (
              <Flex key={`risk-skeleton-1`} direction="column" width="100%">
                {Array.from(Array(3)).map((_) => (
                  <>
                    <Flex
                      paddingTop="24px"
                      paddingBottom="24px"
                      style={{
                        borderBottom: "1px solid",
                        borderTop: "1px solid",
                        borderColor: "#E0E4E8",
                      }}
                      alignItems="center"
                    >
                      <SkeletonCircle size="5" />
                      <Skeleton
                        speed={1.8}
                        startColor="dmazespecial.400"
                        endColor="dmazegray.200"
                        width="45%"
                        marginLeft="18px"
                        height="14px"
                      >
                        Please wait
                      </Skeleton>
                    </Flex>
                    <Box
                      marginLeft="20px"
                      paddingBottom="24px"
                      paddingTop="24px"
                    >
                      <List>
                        {Array.from(Array(2)).map((_, index) => (
                          <ListItem
                            key={`measure-${index}`}
                            display="flex"
                            marginTop="16px"
                          >
                            <ListIcon
                              verticalAlign="unset"
                              boxSize="9px"
                              as={CircleIcon}
                              color="dmazespecial.400"
                            />
                            <SkeletonText width="100%">
                              Please Wait
                            </SkeletonText>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </>
                ))}
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
    </>
  )
}
